import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { Col, Flex, Form, Row, Select, Space, Tooltip } from 'antd';
import { ScopeButton } from "@monorepo/shared-lib";
import { StyledCard } from 'components/Custom/card';
import { FormInput } from 'components/Custom/inputs';
import { LabelOptionalSuffix, LabelWrapper } from 'components/Custom/labels';

import { FormSelect } from 'components/Custom/selects';
import { CardDescBoldSpan, CardDescSpan, CardTitleSpan } from 'components/Custom/spans';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { Currency } from 'store/api/enums';
import { BillingFormRequest } from 'store/billing/models/billingFormRequest';
import { Country, CountryFlag } from 'store/common/models';
import { CountryFlagImage, renderPhoneCodeOption } from 'utils/render-countries-codes';
import { ReactComponent as IconQUestionMark } from 'assets/images/icons/iconQuestionMark.svg';

interface BillingFormProps {
  billingForm?: any;
  countries: Country[];
  countriesExternalData: CountryFlag[];
  isCheckoutForm?: boolean;
  changeCurrency?: (newCurrency) => void;
  isLoading?: boolean;
  isAllowed?: boolean;
  saveBillingDetails?: (billingDetails: BillingFormRequest) => void;
  userVatNumber?: string;
  setCountryISO: (countryISO: string) => void;
  setUserVatNumber: (userVatNumber: string) => void;
  isEurCountry: boolean;
}

interface BillingFormValues {
  addressLine1: string;
  addressLine2: string;
  town: string;
  country: string;
  zipCode: string;
  code?: string;
  phonePrefix?: string;
  phone?: string;
  email?: string;
  currency: string;
  vatNumber: string;
  companyName?: string;
}

export const BillingForm: React.FC<BillingFormProps> = ({
  billingForm,
  countries,
  countriesExternalData,
  isCheckoutForm,
  changeCurrency,
  isLoading,
  isAllowed,
  saveBillingDetails,
  userVatNumber,
  setCountryISO,
  setUserVatNumber,
  isEurCountry,
}) => {
  const { t } = useTranslation();
  const { localizeCountry, language } = useCountryLabel();
  const [countriesWithFlags, setCountriesWithFlags] = useState<Country[]>([]);

  useEffect(() => {
    if (countries && countriesExternalData) {
      const newCountriesWithFlags = countries.map((country) => ({
        ...country,
        flag: countriesExternalData.find(
          (flag) => flag.cca2.toLowerCase() === country.iso.toLowerCase()
        )?.flags.png,
      }));
      setCountriesWithFlags(newCountriesWithFlags);
    }
  }, [countries, countriesExternalData]);

  const handleSubmit = (values: BillingFormValues) => {
    // to dispatch action for saving billing form values
    const billingDetails: BillingFormRequest = {
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      zipCode: values.zipCode ?? '',
      companyName: values.companyName ?? '',
      country: values.country,
      currency: values.currency,
      email: values.email ?? '',
      phonePrefix: values.phonePrefix ?? '',
      phone: values.phone ?? '',
      state: values.town,
      //vatNumber: values.vatNumber,
      vatNumber: userVatNumber ?? '',
      town: values.town,
    };
    saveBillingDetails && saveBillingDetails(billingDetails);
  };

  const renderCountryWithFlag = (country: Country): React.ReactNode => {
    return (
      <div style={{ display: 'flex' }}>
        <CountryFlagImage
          src={country.flag}
          alt={country.name}
          cca2={country.iso ?? ''}
        />
        {localizeCountry(country.iso, country.name)}
      </div>
    );
  };

  const renderCurrencyList = () => {
    return Object.values(Currency).map((curr) => (
      <Select.Option key={curr} value={curr}>
        {curr}
      </Select.Option>
    ));
  };

  const onCurrencyChange = (value) => {
    changeCurrency && changeCurrency(value);
  };

  const onCountryChange = (_value, option) => {
    const countryISO = option.iso3;
    setCountryISO(countryISO);
  };

  const renderFormLabel = (text: string, optional = false) => (
    <LabelWrapper>
      <span className="label-text">{text}</span>
      {optional && (
        <>
          {' '}
          <LabelOptionalSuffix>{t('organisation.optional')}</LabelOptionalSuffix>
        </>
      )}
    </LabelWrapper>
  );

  /* const renderSuffix = () => {
    //const vatNumber = billingForm.getFieldValue('vat');
    if (isVATNumberValid && !isEmptyVAT && !isLoading && isEurCountry) {
      return <CheckCircleTwoTone twoToneColor="#52c41a" />;
    } else if (!isVATNumberValid && !isEmptyVAT && !isLoading && isEurCountry) {
      return (
        <>
          <StyledTooltip // @ts-ignore
            title={'Invalid VAT number!'}
            placement="top"
            arrow={false}
          >
            <CloseCircleTwoTone twoTonecolor="var(--color-status-danger, #D13131)" />
          </StyledTooltip>
        </>
      );
    } else {
      return <></>;
    }
  }; */

  return (
    <StyledCard //@ts-ignore
      $stopshadowbox
      $borderless={isCheckoutForm}
      //width={!isCheckoutForm ? '773px' : '100%'}
      height={'640px'}
      style={{
        marginTop: '25px',
        //maxWidth: !isCheckoutForm ? 'none' : 'calc(100vw - 650px)',
      }}
    >
      <Space direction="vertical" size={isCheckoutForm ? 4 : 8}>
        {isCheckoutForm ? (
          <>
            <CardDescBoldSpan
              fontSize="18px"
              fontWeight="600"
              $lineHeight="24px"
              style={{ display: 'block' }}
            >
              {t('billing.need-details')}
            </CardDescBoldSpan>
            <CardDescSpan style={{ display: 'block', maxWidth: 520 }}>
              {t('billing.finalize-price')}
            </CardDescSpan>
          </>
        ) : (
          <CardTitleSpan>{t('users.billing-details')}</CardTitleSpan>
        )}
      </Space>
      <Form
        form={billingForm}
        onFinish={handleSubmit}
        style={{ paddingTop: '40px' }}
        layout="vertical"
        requiredMark={false}
      >
        <Row justify={'center'} align={'middle'}>
          <Col span={12}>
            {/* <LabelWrapper>
              <span className="label-text">{t('signup.address-line1')}</span>
            </LabelWrapper> */}
            <Form.Item
              label={renderFormLabel(t('signup.address-line1'))}
              name="addressLine1"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormInput
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={renderFormLabel(t('signup.address-line2'), true)}
              name="addressLine2"
            >
              <FormInput
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={renderFormLabel(t('signup.town'))}
              name="town"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormInput
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={renderFormLabel(t('common.country'))}
              name="country"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormSelect //@ts-ignore
                width="95%"
                height="45px"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
                bordercolor={
                  billingForm.getFieldError('country')[0]?.length > 0
                    ? '#b94a48'
                    : 'var(--color-border, #dbeae3)'
                }
                showSearch={true}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={onCountryChange}
              >
                {countriesWithFlags &&
                  countriesWithFlags.map((c) => (
                    <Select.Option
                      key={c.iso}
                      value={c.name}
                      label={c.name}
                      iso3={c.iso3}
                    >
                      {renderCountryWithFlag(c)}
                    </Select.Option>
                  ))}
              </FormSelect>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={renderFormLabel(t('signup.zip-code'))}
              name="zipCode"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormInput
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>

          {
            //!isCheckoutForm && (
            <>
              <Col span={12} style={{ display: 'flex' }}>
                <div style={{ width: '28%', marginRight: '2%' }}>
                  <Form.Item
                    label={renderFormLabel(t('signup.code'))}
                    name="phonePrefix"
                    rules={[
                      { required: true, message: t('common.required-field') },
                    ]}
                  >
                    <FormSelect //@ts-ignore
                      width={'95%'}
                      height="45px"
                      bordercolor={
                        billingForm.getFieldError('code')[0]?.length > 0
                          ? '#b94a48'
                          : 'var(--color-border, #dbeae3)'
                      }
                      styles={{ popup: { minWidth: '330px' } }}
                      optionLabelProp="value"
                      //id="counties-code"
                      bgcolor="var(--color-surface-raised, #FFFFFF)"
                      showSearch={true}
                      filterOption={(input, option) =>
                        option?.value
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {countriesExternalData &&
                        countriesExternalData.map((c) =>
                          renderPhoneCodeOption(c, language)
                        )}
                    </FormSelect>
                  </Form.Item>
                </div>
                <div style={{ width: '68%' }}>
                  <Form.Item
                    label={renderFormLabel(t('contacts.phone'))}
                    name="phone"
                    rules={[
                      { required: true, message: t('common.required-field') },
                    ]}
                  >
                    <FormInput
                      width={'95%'} //@ts-ignore
                      border_color="var(--color-border, #dbeae3)"
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={renderFormLabel(t('signup.email-address'))}
                  name="email"
                  rules={[
                    { required: true, message: t('common.required-field') },
                    {
                      type: 'email',
                      message: t('signup.invalid-email'),
                    },
                  ]}
                >
                  <FormInput
                    width="95%" //@ts-ignore
                    border_color="var(--color-border, #dbeae3)"
                  />
                </Form.Item>
              </Col>
            </>
            /*)*/
          }

          {
            //isCheckoutForm && (
            <Col span={12}>
              <Form.Item
                label={renderFormLabel(t('common.currency'))}
                name="currency"
                rules={[
                  { required: true, message: t('common.required-field') },
                ]}
                //style={{ width: '29%', marginRight: '2%' }}
              >
                <FormSelect //@ts-ignore
                  width={'95%'}
                  height="45px"
                  bordercolor={
                    billingForm.getFieldError('currency')[0]?.length > 0
                      ? '#b94a48'
                      : 'var(--color-border, #dbeae3)'
                  }
                  styles={{ popup: { minWidth: '330px' } }}
                  optionLabelProp="value"
                  //id="counties-code"
                  bgcolor="var(--color-surface-raised, #FFFFFF)"
                  onChange={onCurrencyChange}
                >
                  {renderCurrencyList()}
                </FormSelect>
              </Form.Item>
            </Col>
            /*)*/
          }

          <Col span={12}>
            <Form.Item
              label={renderFormLabel(t('signup.company-name'), true)}
              name="companyName"
              rules={[{ required: false }]}
            >
              <FormInput
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <LabelWrapper>
                  <span className="label-text">{t('main.VAT')}</span>{' '}
                  <LabelOptionalSuffix>{t('organisation.optional')}</LabelOptionalSuffix>
                  {isCheckoutForm && (
                    <Tooltip
                      title={
                        <div style={{ maxWidth: '400px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            {t('billing.vat-tooltip-intro')}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            {t('billing.vat-tooltip-examples-title')}
                          </div>
                          <div style={{ marginBottom: '4px' }}>
                            {t('billing.vat-example-cyprus')}
                          </div>
                          <div style={{ marginBottom: '4px' }}>
                            {t('billing.vat-example-malta')}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            {t('billing.vat-example-ireland')}
                          </div>
                          <div>{t('billing.vat-tooltip-support')}</div>
                        </div>
                      }
                      overlayClassName="scope-billing-vat-tooltip"
                    >
                      <IconQUestionMark
                        width={15}
                        height={15}
                        style={{ cursor: 'pointer', marginBottom: -3, marginLeft: 4 }}
                      />
                    </Tooltip>
                  )}
                </LabelWrapper>
              }
              name="vatNumber"
              rules={[{ required: false, message: t('common.required-field') }]}
              style={{ marginBottom: '24px', height: '80px' }} // Fixed height for the entire form item
            >
              <FormInput
                /* status={
                  !isVATNumberValid && !isEmptyVAT && !isLoading && isEurCountry
                    ? 'error'
                    : ''
                } */
                width="95%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
                //onBlur={onBlurVATNumber}
                onChange={(e) => setUserVatNumber(e.target.value)}
                //suffix={renderSuffix()}
                loading={isLoading}
                id="vatNumber"
                value={userVatNumber}
              />
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted, #5a6a6a)',
                  marginTop: '4px',
                  marginLeft: '4px',
                  opacity: userVatNumber && !isEurCountry ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                {t('billing.non-eu-vat-note')}
              </div>
            </Form.Item>
          </Col>
          <Col span={12}></Col>
        </Row>

        {!isCheckoutForm ? (
          <Form.Item>
            <ScopeButton //@ts-ignore
              width="172px"
              height="40px"
              type="primary"
              htmlType="submit"
              disabled={!isAllowed}
            >
              {t('common.save')}
            </ScopeButton>
          </Form.Item>
        ) : (
          <Flex justify="space-between" align="center">
            <ScopeButton //@ts-ignore
              width="172px"
              height="40px"
              type="default"
              htmlType="reset"
            >
              {t('billing.clear-form')}
            </ScopeButton>
          </Flex>
        )}
      </Form>
    </StyledCard>
  );
};

